import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collegesAPI } from '../../services/api';

const AVATAR_COLORS = [
  '#1a5d3a', '#2E7D32', '#1565C0', '#6A1B9A',
  '#C62828', '#E65100', '#00695C', '#283593',
];

const getInitials = (name = '') => {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
};

const getAvatarColor = (name = '') => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const CollegeInitialsAvatar = ({ name }) => (
  <div
    className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 text-white font-bold text-sm"
    style={{ backgroundColor: getAvatarColor(name) }}
    aria-hidden="true"
  >
    {getInitials(name)}
  </div>
);

const TopCollegeMatches = () => {
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    collegesAPI
      .getSavedColleges()
      .then((data) => setColleges(Array.isArray(data) ? data : []))
      .catch(() => setColleges([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="top-college-matches">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Saved Colleges</h2>
        <Link
          to="/colleges"
          className="text-sm font-medium transition-colors"
          style={{ color: '#1a5d3a' }}
          data-testid="colleges-view-all-link"
        >
          View All
        </Link>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((n) => (
            <div key={n} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-gray-200" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : colleges.length === 0 ? (
        <div
          className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 text-center"
          data-testid="colleges-empty-state"
        >
          <p className="text-sm text-gray-500 mb-4">No colleges saved yet</p>
          <Link
            to="/colleges"
            className="inline-block text-sm font-medium px-4 py-2 rounded-lg text-white"
            style={{ backgroundColor: '#1a5d3a' }}
            data-testid="colleges-explore-link"
          >
            Explore Colleges
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="colleges-list">
          {colleges.slice(0, 6).map((college) => (
            <Link
              key={college.ipedsId}
              to={`/college/${college.ipedsId}`}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:border-gray-300 transition-colors block"
              data-testid={`college-card-${college.ipedsId}`}
            >
              <div className="flex items-center gap-3">
                <CollegeInitialsAvatar name={college.name} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate">{college.name}</div>
                  {(college.city || college.state) && (
                    <div className="text-xs text-gray-500 mt-0.5 truncate">
                      {[college.city, college.state].filter(Boolean).join(', ')}
                    </div>
                  )}
                  {college.publicPrivate && (
                    <div className="text-xs text-gray-400 mt-0.5">{college.publicPrivate}</div>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default TopCollegeMatches;
